import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import SidebarEscritorio from '../components/sala/SidebarEscritorio';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', moradoClaro:'#AFA9EC' };

// Fallback sample data — only shown if Supabase returns nothing
const SAMPLE_NEWS = [
  { title:'WhatsApp lanza catálogos para negocios pequeños', preview:'WhatsApp Business ahora permite crear catálogos visuales directamente en la app. Los emprendedores pueden mostrar sus productos con fotos, descripciones y precios sin necesidad de un sitio web.', tag:'E-commerce', genio_comment:'¡Órale! Esto es oro para quien vende por WhatsApp. Ya no necesitas mandar fotos una por una.', luca_comment:'Lo que debes hacer es activar tu catálogo hoy. Es gratis y te ahorra horas.', source_url:'https://business.whatsapp.com', image_emoji:'💬' },
  { title:'5 métricas que todo emprendedor debe medir desde el día 1', preview:'Con estas 5 métricas básicas puedes entender si tu negocio está avanzando o estancado: costo de adquisición, retención, ticket promedio, margen y velocidad de crecimiento.', tag:'Negocios', genio_comment:'¿Ya viste esto? Medir no es aburrido, es tu superpoder secreto.', luca_comment:'Esto significa que con 5 números puedes tomar mejores decisiones que el 90% de emprendedores.', source_url:null, image_emoji:'📊' },
  { title:'IA generativa para crear contenido de redes sociales', preview:'Las herramientas de IA están democratizando la creación de contenido. Ahora cualquier emprendedor puede generar imágenes, textos y videos de calidad profesional sin ser diseñador.', tag:'IA', genio_comment:'¡Esto está increíble! Ya no hay excusa para no tener contenido profesional.', luca_comment:'Pon atención a esto: la IA es la herramienta, pero tú defines la estrategia.', source_url:null, image_emoji:'🤖' },
  { title:'Cómo validar tu idea en 48 horas sin gastar un peso', preview:'La validación no requiere dinero, requiere conversaciones. Con 10 entrevistas bien hechas puedes saber si tu idea tiene mercado real.', tag:'Startups', genio_comment:'¡Dos días! Eso es todo lo que necesitas para saber si tu idea vuela.', luca_comment:'Lo que debes hacer es hablar con 10 personas esta semana. Si 7 de 10 tienen el mismo dolor, tienes algo.', source_url:null, image_emoji:'🧪' },
  { title:'Mercado Libre abre programa para emprendedores emergentes', preview:'Nuevo programa de aceleración para vendedores pequeños. Incluye mentoría, visibilidad gratuita y herramientas de análisis por 3 meses.', tag:'E-commerce', genio_comment:'¡Visibilidad gratis en Mercado Libre! Esto no pasa todos los días.', luca_comment:'Las inscripciones están abiertas. Si vendes productos físicos, aplica hoy antes de que se llene.', source_url:null, image_emoji:'🛒' },
  { title:'La regla del 80/20 aplicada a tu negocio', preview:'El 80% de tus resultados viene del 20% de tus acciones. Identifica ese 20% y duplica tu tiempo ahí.', tag:'Negocios', genio_comment:'¿Y si en vez de trabajar más, trabajas en lo correcto? Ese es el truco.', luca_comment:'Revisa tus últimos 30 días: qué actividades generaron más ingresos. Duplica eso y delega el resto.', source_url:null, image_emoji:'🎯' },
];

export default function NewsletterPage() {
  const { profile, membership } = useAuth();
  const { isMobile, w } = useScreen();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [current, setCurrent] = useState(0);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);

  // Fetch real articles from Supabase via API
  useEffect(() => {
    fetch('/api/get-newsletter')
      .then(r => r.json())
      .then(data => {
        if (data.articles && data.articles.length > 0) {
          setNews(data.articles);
        } else {
          setNews(SAMPLE_NEWS);
        }
      })
      .catch(() => setNews(SAMPLE_NEWS))
      .finally(() => setLoading(false));
  }, []);

  const scrollToIndex = useCallback((idx) => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.children[idx];
    if (card) {
      card.scrollIntoView({ behavior:'smooth', inline:'start', block:'nearest' });
      setCurrent(idx);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || isDragging.current) return;
    const cardWidth = container.children[0]?.offsetWidth || 1;
    const gap = 16;
    const idx = Math.round(container.scrollLeft / (cardWidth + gap));
    setCurrent(Math.min(idx, news.length - 1));
  }, [news.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive:true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Mouse drag
  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };
  const onMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
    setTimeout(handleScroll, 100);
  };

  const gradients = [
    'linear-gradient(135deg, rgba(93,193,185,0.4), rgba(83,74,183,0.3))',
    'linear-gradient(135deg, rgba(253,190,2,0.3), rgba(235,99,107,0.25))',
    'linear-gradient(135deg, rgba(83,74,183,0.35), rgba(93,193,185,0.3))',
    'linear-gradient(135deg, rgba(235,99,107,0.3), rgba(253,190,2,0.25))',
    'linear-gradient(135deg, rgba(93,193,185,0.35), rgba(253,190,2,0.3))',
    'linear-gradient(135deg, rgba(83,74,183,0.3), rgba(235,99,107,0.25))',
  ];

  const hasValidUrl = (url) => url && url !== '#' && url.startsWith('http');

  return (
    <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff' }}>
      {/* Header */}
      <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {isMobile && <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background:'none', border:'none', color:'#fff', fontSize:18, cursor:'pointer' }}>☰</button>}
          <span style={{ fontFamily:serif, fontSize:17, background:'linear-gradient(90deg, #5dc1b9, #FDBE02, #eb636b)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>T&D</span>
          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:999, background:'rgba(253,190,2,0.15)', color:C.mango }}>Newsletter</span>
        </div>
        <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg, #534AB7, #1D9E75)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600 }}>
          {(profile?.full_name || 'U')[0]}
        </div>
      </header>

      <div style={{ display:'flex', minHeight:'calc(100vh - 50px)' }}>
        {/* Sidebar */}
        {w >= 900 && (sidebarOpen || !isMobile) && (
          <SidebarEscritorio membership={membership} currentView="newsletter" />
        )}

        {/* Main */}
        <main style={{ flex:1, padding:isMobile?'16px 0':'20px 0', overflowX:'hidden' }}>
          <div style={{ padding:isMobile?'0 12px':'0 24px', marginBottom:20 }}>
            <p style={{ fontSize:10, color:'rgba(253,190,2,0.8)', letterSpacing:1.5, textTransform:'uppercase', margin:'0 0 4px' }}>Curadas por Genio & Luca</p>
            <h1 style={{ fontFamily:serif, fontSize:22, fontWeight:400, margin:'0 0 6px' }}>Newsletter de esta semana</h1>
            <p style={{ margin:'0 0 10px', fontSize:14, color:'rgba(255,255,255,0.55)' }}>{news.length} noticias curadas por Genio y Luca</p>
            <p style={{ margin:0, fontSize:12, color:'rgba(255,255,255,0.35)', lineHeight:1.6, fontStyle:'italic' }}>
              Genio (curiosidad) y Luca (accion) son nuestros agentes IA. Cada semana eligen noticias relevantes para ti usando Claude AI.
            </p>
          </div>

          {/* Loading state */}
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:'60px 0' }}>
              <div style={{ width:32, height:32, border:'3px solid rgba(255,255,255,0.1)', borderTopColor:C.turquesa, borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
            </div>
          )}

          {!loading && news.length > 0 && (
            <>
              {/* Navigation arrows (desktop) */}
              {!isMobile && (
                <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'0 24px', marginBottom:12 }}>
                  <button onClick={() => scrollToIndex(Math.max(0, current - 1))} disabled={current === 0} style={{
                    width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(255,255,255,0.12)',
                    color: current === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)', fontSize:16, cursor: current === 0 ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                  }}>←</button>
                  <button onClick={() => scrollToIndex(Math.min(news.length - 1, current + 1))} disabled={current === news.length - 1} style={{
                    width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(255,255,255,0.12)',
                    color: current === news.length - 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)', fontSize:16, cursor: current === news.length - 1 ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                  }}>→</button>
                </div>
              )}

              {/* Carousel */}
              <div
                ref={scrollRef}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                style={{
                  display:'flex',
                  gap:16,
                  overflowX:'auto',
                  scrollSnapType:'x mandatory',
                  scrollBehavior:'smooth',
                  padding:isMobile?'0 12px':'0 24px',
                  cursor:'grab',
                  WebkitOverflowScrolling:'touch',
                  msOverflowStyle:'none',
                  scrollbarWidth:'none',
                }}
              >
                {news.map((item, i) => {
                  const isGenio = i % 2 === 0;
                  const emoji = item.image_emoji || '✨';
                  return (
                    <div key={item.id || i} style={{
                      flex:'0 0 calc(100% - 16px)',
                      maxWidth: isMobile ? 'calc(100% - 16px)' : 560,
                      scrollSnapAlign:'start',
                      background:'rgba(255,255,255,0.05)',
                      backdropFilter:'blur(30px)',
                      WebkitBackdropFilter:'blur(30px)',
                      border:'1px solid rgba(255,255,255,0.1)',
                      borderRadius:24,
                      overflow:'hidden',
                      userSelect:'none',
                    }}>
                      {/* Emoji hero */}
                      <div style={{
                        height:180,
                        background:gradients[i % gradients.length],
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:72,
                        filter:'drop-shadow(0 4px 20px rgba(255,255,255,0.2))',
                      }}>
                        {emoji}
                      </div>

                      <div style={{ padding:isMobile?'18px 16px':'22px 24px' }}>
                        {/* Tag pill */}
                        <span style={{
                          display:'inline-block',
                          padding:'4px 12px',
                          background: isGenio ? 'rgba(253,190,2,0.2)' : 'rgba(93,193,185,0.2)',
                          color: isGenio ? C.mango : C.turquesa,
                          borderRadius:999,
                          fontSize:11,
                          fontWeight:500,
                          marginBottom:12,
                        }}>
                          {item.tag || (isGenio ? 'Genio destaca' : 'Luca aterriza')}
                        </span>

                        {/* Title */}
                        <h2 style={{ fontFamily:serif, fontSize:20, fontWeight:400, margin:'0 0 14px', lineHeight:1.4 }}>{item.title}</h2>

                        {/* Preview / Summary */}
                        <div style={{ fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.7, marginBottom:16 }}>
                          {(item.preview || '').split('\n\n').map((p, j) => (
                            <p key={j} style={{ margin: j === 0 ? 0 : '10px 0 0' }}>{p}</p>
                          ))}
                        </div>

                        {/* Genio comment */}
                        {item.genio_comment && (
                          <div style={{
                            padding:'14px 16px',
                            background:'rgba(253,190,2,0.06)',
                            borderRadius:12,
                            marginBottom:10,
                            borderLeft:`2px solid ${C.mango}`,
                          }}>
                            <p style={{ margin:'0 0 4px', fontSize:10, color:'rgba(253,190,2,0.7)', letterSpacing:1, textTransform:'uppercase' }}>Genio dice</p>
                            <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.85)', fontStyle:'italic', lineHeight:1.6 }}>{item.genio_comment}</p>
                          </div>
                        )}

                        {/* Luca comment */}
                        {item.luca_comment && (
                          <div style={{
                            padding:'14px 16px',
                            background:'rgba(93,193,185,0.06)',
                            borderRadius:12,
                            marginBottom:16,
                            borderLeft:`2px solid ${C.turquesa}`,
                          }}>
                            <p style={{ margin:'0 0 4px', fontSize:10, color:'rgba(93,193,185,0.7)', letterSpacing:1, textTransform:'uppercase' }}>Luca dice</p>
                            <p style={{ margin:0, fontSize:14, color:'rgba(255,255,255,0.85)', fontStyle:'italic', lineHeight:1.6 }}>{item.luca_comment}</p>
                          </div>
                        )}

                        {/* Source link — only if valid URL */}
                        {hasValidUrl(item.source_url) && (
                          <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{
                            color:C.turquesa, fontSize:13, textDecoration:'none', fontWeight:500,
                          }}>
                            Ver fuente original →
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Dots */}
              <div style={{ display:'flex', justifyContent:'center', gap:8, padding:'20px 0' }}>
                {news.map((_, i) => (
                  <button key={i} onClick={() => scrollToIndex(i)} style={{
                    width: current === i ? 20 : 8, height:8,
                    borderRadius:4,
                    background: current === i ? C.turquesa : 'rgba(255,255,255,0.2)',
                    border:'none',
                    cursor:'pointer',
                    transition:'all 0.3s ease',
                    padding:0,
                  }} />
                ))}
              </div>
            </>
          )}

          {/* Empty state */}
          {!loading && news.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 20px' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
              <p style={{ fontSize:16, color:'rgba(255,255,255,0.6)', margin:0 }}>Aun no hay noticias esta semana. Genio y Luca estan preparando algo bueno.</p>
            </div>
          )}
        </main>
      </div>

      {/* Hide scrollbar + spinner animation */}
      <style>{`div::-webkit-scrollbar { display: none; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
